import * as React from 'react'
import { StoreProps, castingStoreGetDurationForAllowedPattern, castingStoreGetFirstEnabledDateFrom, castingStoreGetMinStartDate, castingStoreHasRealTimeAvailabilityAtTime, castingStoreIsComplex, castingStoreIsRealTime } from '../../../../services/stores/casting.store'
import { moment } from '../../../../services/utils/moment.service'
import { formatTime, parseTime, utilRange } from '../../../../services/utils/utils.service'
import { UnserializedMultiOpeningHours, openableDateToWorkingDay, openableStartHours, openableStartHoursAtDate } from '../../../../services/utils/openables.service'
import { FloatingContainerTitle } from '../casting-styled.component'
import CastingStartDateSkipWaitingOrderHoursPeriodComponent from './casting-start-date-skip-waiting-order-hours-period.component'
import { StyledDayPicker, getDayPickerDateLocale } from '../../../shared/styled-day-picker.component'
import CastingTimePickerComponent from './casting-time-picker-component.compoent'
import { I18nService } from '../../../../services/utils/i18n.service'

type Props = StoreProps & {
  initialMonth: Date
  fromMonth: Date
  startDate: Date
  multiOpeningHours: UnserializedMultiOpeningHours & { reduced: true, midnightSafe: true }
  isAllowingAnyTime: boolean
  isStartDayDisabled: (date: Date) => boolean
}

const i18nScoped = new I18nService(require('./casting-start-date-shorter-slots.component.yml'))

export default function CastingStartDateShorterSlotsComponent ({ store, data, initialMonth, fromMonth, startDate, multiOpeningHours, isAllowingAnyTime, isStartDayDisabled }: Props) {
  const { castingType, offeredService: os, slots } = data
  const flexibleStartDateInAbsenceOfStartTimeAndDuration = os.no_start_time_nor_duration_asked_to_customer && os.flexible_start_date_in_absence_of_start_time_and_duration
  const startTime = React.useMemo(() => slots[0].allowed_patterns.length ? parseTime(slots[0].allowed_patterns[0].from_hour) : null, [slots])
  const endTime = React.useMemo(() => slots[0].allowed_patterns.length && slots[0].allowed_patterns[0].until_hour ? parseTime(slots[0].allowed_patterns[0].until_hour) : null, [slots])

  const setStartDate = React.useCallback((startDate: Date) => {
    const date = moment(startDate).format('YYYY-MM-DD')
    const body: Partial<StoreProps['data']> = {}

    body.slots = slots.map(slot => ({ ...slot, start_date: date, end_date: null as string }))

    if (os.no_start_time_nor_duration_asked_to_customer) {
      body.slots[0].allowed_patterns = os.flexible_start_date_in_absence_of_start_time_and_duration
        ? ([0, 1, 2, 3, 4, 5, 6] as const).map(dayId => ({ day_id: dayId, from_hour: '00:00', until_hour: '24:00', additional_days: null as number }))
        : [{ day_id: startDate.getDay(), from_hour: '00:00', until_hour: '24:00', additional_days: null }]
    } else if (castingType === 'casting_one_shot') {
      body.slots[0].allowed_patterns = []
    }

    store.set(body)

    if (castingType === 'casting_recurring') store.nextFocus({ closeOnMobile: true })
  }, [castingType, os.flexible_start_date_in_absence_of_start_time_and_duration, os.no_start_time_nor_duration_asked_to_customer, slots, store])

  const getStartTimeList = React.useCallback((startDate: Date) => {
    const isRealtime = castingStoreIsRealTime(data)
    const compactStartHours = openableStartHoursAtDate(
      startDate,
      openableStartHours(multiOpeningHours, castingStoreGetDurationForAllowedPattern(data) / 3600),
      data.backofficeSkipsWaitingOrderHoursPeriod ? 1 : os.waiting_order_hours_period, 
      os.duration_step / 3600
    )

    return compactStartHours
      .flatMap(moh => utilRange(moh[0], moh[1] + 0.001, os.duration_step / 3600))
      .filter(hour => !isRealtime || castingStoreHasRealTimeAvailabilityAtTime(data, store, moment(startDate).hours(hour).minutes(hour * 60 % 60), slots[0].duration))
  }, [data, multiOpeningHours, os.duration_step, os.waiting_order_hours_period, slots, store])

  const getEndTimeList = React.useCallback((startDate: Date, startTime: number) => {
    const currentMoh = multiOpeningHours[openableDateToWorkingDay(startDate)].find(moh => moh[0] <= startTime && startTime <= moh[1])
    return utilRange(startTime + castingStoreGetDurationForAllowedPattern(data) / 3600, currentMoh[1] + 0.001, os.duration_step / 3600)
  }, [data, multiOpeningHours, os.duration_step])

  const setStartTime = React.useCallback((startDate: Date, newTime: number) => {
    const previousHour = slots[0].allowed_patterns.length && slots[0].allowed_patterns[0].from_hour ? Math.trunc(parseTime(slots[0].allowed_patterns[0].from_hour)) : null
    const newSlots = [{ ...slots[0] }]
    newSlots[0].allowed_patterns = [{
      day_id: moment(slots[0].start_date).day(),
      from_hour: formatTime(newTime),
      until_hour: os['customer_choose_end_date?'] ? null : formatTime((newTime + slots[0].duration / 3600) % 24),
      additional_days: null as number
    }]

    store.setSlots(newSlots)

    // customer should still set end time on this screen
    if (os['customer_choose_end_date?'] && castingType === 'casting_one_shot' && !castingStoreIsComplex(data)) return

    // If hour did not changed => it means customer changed minutes OR customer cannot choice another minute step
    if (previousHour === Math.trunc(newTime) || getStartTimeList(startDate).filter(time => Math.trunc(time) === Math.trunc(newTime)).length === 1) store.nextFocus({ closeOnMobile: true })
  }, [castingType, data, getStartTimeList, os, slots, store])

  const setEndTime = React.useCallback((startDate: Date, startTime: number, endTime: number) => {
    const previousHour = slots[0].allowed_patterns.length && slots[0].allowed_patterns[0].until_hour ? Math.trunc(parseTime(slots[0].allowed_patterns[0].until_hour)) : null
    store.setSlots([{
      ...slots[0],
      duration: 3600 * ((endTime - startTime + 24) % 24),
      allowed_patterns: [{ ...slots[0].allowed_patterns[0], until_hour: formatTime(endTime) }]
    }])

    // If hour did not changed => it means customer changed minutes OR customer cannot choice another minute step
    if (previousHour === Math.trunc(endTime) || getEndTimeList(startDate, startTime).filter(time => Math.trunc(time) === Math.trunc(endTime)).length === 1) store.nextFocus({ closeOnMobile: true })
  }, [getEndTimeList, slots, store])

  const toggleAllowAnyTime = React.useCallback(() => {
    const firstPossibleStartDate = castingStoreGetFirstEnabledDateFrom(castingStoreGetMinStartDate(data).toDate(), isStartDayDisabled)
    store.toggleAllowAnyTime(moment(firstPossibleStartDate).add(1, 'day').format('YYYY-MM-DD'))
    store.nextFocus({ closeOnMobile: true })
  }, [data, isStartDayDisabled, store])

  return <>
    <FloatingContainerTitle>{ i18nScoped.t(flexibleStartDateInAbsenceOfStartTimeAndDuration ? 'pick_a_prefered_date' : 'pick_a_date') }</FloatingContainerTitle>

    { /**
      * In complex mode: the user has no other choice than selecting exact time changes.
      * In recurring mode: the user selects days and periods in the grid-based UI.
      * In flexible day (24h/7d generated allowed patterns), the that radio is virtually toggled on and cannot be disabled. */ }
    { !castingStoreIsComplex(data)
      && castingType === 'casting_one_shot'
      && !flexibleStartDateInAbsenceOfStartTimeAndDuration
      && <div className="inputRadio">
        <input id="no-timespan" type="checkbox" checked={ isAllowingAnyTime } onChange={ toggleAllowAnyTime } />
        <label htmlFor="no-timespan">{ i18nScoped.t('no_timespan') }</label>
      </div> }

    <CastingStartDateSkipWaitingOrderHoursPeriodComponent data={ data } store={ store } />

    <StyledDayPicker
      mode='single'
      defaultMonth={ initialMonth }
      startMonth={ fromMonth }
      locale={ getDayPickerDateLocale() }
      selected={ startDate }
      disabled={ isStartDayDisabled }
      onSelect={ setStartDate } />

    { castingType === 'casting_one_shot' && startDate && !os.no_start_time_nor_duration_asked_to_customer && <>
      <div style={{ marginTop: '1em' }}>
        <FloatingContainerTitle>{ i18nScoped.t(os['customer_choose_end_date?'] ? 'start_hour' : 'start_from', { start_time: startTime ? formatTime(startTime) : '' }) }</FloatingContainerTitle>
        <CastingTimePickerComponent name="startPicker" values={ getStartTimeList(startDate) } value={ startTime } onChange={ value => setStartTime(startDate, value) } />
      </div>

      { typeof startTime === 'number' && os['customer_choose_end_date?'] && <div style={{ marginTop: '1em' }}>
        <FloatingContainerTitle>{ i18nScoped.t('end_hour', { end_time: endTime ? formatTime(endTime) : '' }) }</FloatingContainerTitle>
        <CastingTimePickerComponent name="endPicker" values={ getEndTimeList(startDate, startTime) } value={ endTime } onChange={ value => setEndTime(startDate, startTime, value) } />
      </div> }
    </> }
  </>
}